import { useEffect, useMemo, useState, type ReactNode } from "react";
import type { WidgetDataResolveResponse } from "@playbookai/types";
import { useWidgetData, type UseWidgetDataArgs } from "./use-widget-data.js";

/** Host read boundary for an unsaved selection. Separate cache key, no writes. */
export function WidgetDataPreview({
  children,
  ...args
}: UseWidgetDataArgs & {
  readonly children: (
    data: WidgetDataResolveResponse | undefined,
    loading: boolean,
    failed: boolean,
  ) => ReactNode;
}): ReactNode {
  const selectionKey = JSON.stringify(args.preview);
  const [settledKey, setSettledKey] = useState(selectionKey);
  useEffect(() => {
    const timer = setTimeout(() => setSettledKey(selectionKey), 300);
    return () => clearTimeout(timer);
  }, [selectionKey]);
  const settledPreview = useMemo(
    () =>
      settledKey
        ? (JSON.parse(settledKey) as UseWidgetDataArgs["preview"])
        : undefined,
    [settledKey],
  );
  const query = useWidgetData({
    ...args,
    ...(settledPreview ? { preview: settledPreview } : {}),
    enabled: args.enabled !== false && settledKey === selectionKey,
  });
  return children(
    settledKey === selectionKey ? query.data : undefined,
    settledKey !== selectionKey || query.isPending,
    settledKey === selectionKey && query.isError,
  );
}
